import { create } from "zustand";

import { getScorecardAnalysisData } from "../../../data/queries/coaching";
import { getAggregatedAttributePerformanceStats } from "../../../data/queries/performanceAnalysis";
import { getLensContent } from "../../../services/lensService";
import { logError } from "../../../utils/error";
import { TimeRange } from "../../../utils/timerange";
import { buildTeamBestPracticesData } from "../utils/buildTeamBestPracticesData";
import { convertTeamBestPracticesToMarkdown } from "../utils/markdownConverter";
import { extractTopExamples } from "../utils/scorecardAnalysisProcessors";

interface FetchParams {
  timeRange: string;
  protocol: string | undefined;
  protocolPath: string | undefined;
  user: string | undefined;
  permittedEmails: string[] | null;
  timezone: string;
  tenantId: string | null;
  includeAttributeStats?: boolean;
  includeLensDefinition?: boolean;
  includeTeamBestPractices?: boolean;
}

interface CoachingContextState {
  attributeStatsContent: string | null;
  lensDefinitionContent: string | null;
  teamBestPracticesContent: string | null;
  attributeStatsLoaded: boolean;
  lensDefinitionLoaded: boolean;
  teamBestPracticesLoaded: boolean;
  lastFetchParams: FetchParams | null;
}

interface CoachingContextActions {
  fetchAttributeStats: (params: FetchParams) => Promise<void>;
  fetchLensDefinition: (params: FetchParams) => Promise<void>;
  fetchTeamBestPractices: (params: FetchParams) => Promise<void>;
  fetchAllContext: (params: FetchParams) => Promise<void>;
  resetCoachingContext: () => void;
  getIsLoadingContext: () => boolean;
  getHasRequiredContext: () => boolean;
  getMissingContextNames: () => string[];
}

type CoachingContextStore = CoachingContextState & CoachingContextActions;

const initialState: CoachingContextState = {
  attributeStatsContent: null,
  lensDefinitionContent: null,
  teamBestPracticesContent: null,
  attributeStatsLoaded: false,
  lensDefinitionLoaded: false,
  teamBestPracticesLoaded: false,
  lastFetchParams: null
};

export const useCoachingContextStore = create<CoachingContextStore>((set, get) => ({
  ...initialState,

  fetchAttributeStats: async (params: FetchParams) => {
    const { protocol, timeRange, timezone, user, permittedEmails } = params;

    if (!protocol || protocol === "all") {
      set({ attributeStatsContent: null, attributeStatsLoaded: true });
      return;
    }

    try {
      const result = await getAggregatedAttributePerformanceStats({
        timeRange: timeRange as TimeRange,
        timezone,
        lensFilter: protocol,
        userFilter: user,
        permittedEmails,
        minCalls: 10
      });

      const statsContent = JSON.stringify(result, null, 2);
      set({ attributeStatsContent: statsContent, attributeStatsLoaded: true });
    } catch (error) {
      logError("Failed to fetch attribute stats", error);
      set({ attributeStatsContent: null, attributeStatsLoaded: true });
    }
  },

  fetchLensDefinition: async (params: FetchParams) => {
    const { protocolPath, tenantId } = params;

    if (!tenantId || !protocolPath) {
      set({ lensDefinitionContent: null, lensDefinitionLoaded: true });
      return;
    }

    try {
      const content = await getLensContent(tenantId, protocolPath);
      set({ lensDefinitionContent: content, lensDefinitionLoaded: true });
    } catch (error) {
      logError("Failed to fetch lens definition", error);
      set({ lensDefinitionContent: null, lensDefinitionLoaded: true });
    }
  },

  fetchTeamBestPractices: async (params: FetchParams) => {
    const { protocol, timeRange, timezone, permittedEmails, tenantId } = params;

    if (!protocol || protocol === "all") {
      set({ teamBestPracticesContent: null, teamBestPracticesLoaded: true });
      return;
    }

    try {
      const rawData = await getScorecardAnalysisData({
        timeRange: timeRange as TimeRange,
        timezone,
        lensFilter: protocol,
        permittedEmails,
        tenantId,
        minScore: 4
      });

      const result = extractTopExamples(rawData, protocol, 5);

      if (!result) {
        set({ teamBestPracticesContent: null, teamBestPracticesLoaded: true });
        return;
      }

      const bestPracticesData = buildTeamBestPracticesData({
        bestPractices: result,
        timeRange: timeRange as TimeRange,
        timezone
      });

      const markdownContent = convertTeamBestPracticesToMarkdown(bestPracticesData);
      set({ teamBestPracticesContent: markdownContent, teamBestPracticesLoaded: true });
    } catch (error) {
      logError("Failed to fetch team best practices", error);
      set({ teamBestPracticesContent: null, teamBestPracticesLoaded: true });
    }
  },

  fetchAllContext: async (params: FetchParams) => {
    const {
      includeAttributeStats = true,
      includeLensDefinition = true,
      includeTeamBestPractices = true
    } = params;

    set({
      attributeStatsLoaded: !includeAttributeStats,
      lensDefinitionLoaded: !includeLensDefinition,
      teamBestPracticesLoaded: !includeTeamBestPractices,
      attributeStatsContent: includeAttributeStats ? null : get().attributeStatsContent,
      lensDefinitionContent: includeLensDefinition ? null : get().lensDefinitionContent,
      teamBestPracticesContent: includeTeamBestPractices ? null : get().teamBestPracticesContent,
      lastFetchParams: params
    });

    const { fetchAttributeStats, fetchLensDefinition, fetchTeamBestPractices } = get();

    const fetchPromises: Promise<void>[] = [];

    if (includeAttributeStats) {
      fetchPromises.push(fetchAttributeStats(params));
    }
    if (includeLensDefinition) {
      fetchPromises.push(fetchLensDefinition(params));
    }
    if (includeTeamBestPractices) {
      fetchPromises.push(fetchTeamBestPractices(params));
    }

    await Promise.all(fetchPromises);
  },

  resetCoachingContext: () => {
    set({
      ...initialState
    });
  },

  getIsLoadingContext: () => {
    const state = get();
    return !state.attributeStatsLoaded || !state.lensDefinitionLoaded || !state.teamBestPracticesLoaded;
  },

  getHasRequiredContext: () => {
    const state = get();
    return !!state.attributeStatsContent && !!state.lensDefinitionContent && !!state.teamBestPracticesContent;
  },

  getMissingContextNames: () => {
    const state = get();
    const missingItems: string[] = [];

    if (!state.lensDefinitionContent) {
      missingItems.push("Conversation Lens Definition");
    }
    if (!state.teamBestPracticesContent) {
      missingItems.push("Team Best Practices");
    }
    if (!state.attributeStatsContent) {
      missingItems.push("Agent Performance Analysis");
    }

    return missingItems;
  }
}));
